import React, { useEffect, useState } from "react";
import { type Root, createRoot } from "react-dom/client";

import {
  type Configuration,
  type Culture,
  type LayoutModel,
  type ServiceCollection,
  setConfiguration,
} from "@p2l/config";
import { LAYOUT_CHANGE_EVENT } from "@p2l/utils";
import { PublicUiContainer, defaultLayout } from "@p2l/ui-public";

import Footer from "./footer";

import "@p2l/window-fn-types";
import { configureHttp } from "services/http-service";

let root: Root;

export interface ArgsProps {
  elementId: string;
  layout: LayoutModel;
  shouldReloadOnLanguageChange?: boolean;
}

export const FooterAppWithPublicUIContext = (props: {
  culture: Culture;
  layout: LayoutModel;
  shouldReloadOnLanguageChange?: boolean;
}) => {
  const [layout, setLayout] = useState(
    props.layout
      ? {
          ...defaultLayout,
          ...props.layout,
          feedbackName: {
            ...defaultLayout.feedbackName,
            ...props.layout.feedbackName,
          },
        }
      : defaultLayout
  );

  useEffect(() => {
    const layoutChangeListener = {
      handleEvent: (evt: CustomEvent) => {
        setLayout(
          evt.detail.layout
            ? {
                ...defaultLayout,
                ...evt.detail.layout,
                feedbackName: {
                  ...defaultLayout.feedbackName,
                  ...evt.detail.layout.feedbackName,
                },
              }
            : defaultLayout
        );
      },
    };

    window.addEventListener(LAYOUT_CHANGE_EVENT, layoutChangeListener);

    return () => {
      window.removeEventListener(LAYOUT_CHANGE_EVENT, layoutChangeListener);
    };
  }, []);

  return (
    <PublicUiContainer culture={props.culture} layout={layout} noGlobalStyles>
      <Footer
        shouldReloadOnLanguageChange={props.shouldReloadOnLanguageChange}
        layout={layout}
      />
    </PublicUiContainer>
  );
};

export const bootstrap = (
  config: Configuration,
  serviceCollection: ServiceCollection,
  args: ArgsProps
) => {
  // TO-DO this is a temporary fix to prevent the topar from crashing
  // due to missing translation for other lanugage than da-DK and en-GB
  let culture = (config.culture === "da-DK" ? "da-DK" : "en-GB") as Culture; // @ts-ignore

  setConfiguration(serviceCollection, config, { api: [], ui: [] });
  root = createRoot(document.getElementById(args.elementId)!);
  configureHttp();

  root.render(
    <FooterAppWithPublicUIContext
      culture={culture}
      layout={args.layout}
      shouldReloadOnLanguageChange={args.shouldReloadOnLanguageChange}
    />
  );
};

export const destroy = (elementId: string) => {
  const element = document.getElementById(elementId);
  if (element !== null) {
    root.unmount();
  }
};
